
import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Info, Heart, ChevronUp, ChevronDown } from 'lucide-react';
import { Label } from "@/components/ui/label";
import { motion } from 'framer-motion';
import { NutritionJournal } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { triggerLisaChat } from '@/utils/onboardingEvents';
import { useThrottle } from '@/hooks/useDebounce';

interface NotesTabProps {
  journal: NutritionJournal;
  setJournal: (journal: NutritionJournal) => void;
  containerVariants: any;
  itemVariants: any;
}

const saveAttempts = new Map<string, number>();
const MAX_SAVE_ATTEMPTS = 3;
const SAVE_ATTEMPT_TIMEOUT = 60000; // 1 minute reset timeout
const MIN_SAVE_INTERVAL = 10000; // 10 seconds minimum between saves

const NotesTab: React.FC<NotesTabProps> = ({
  journal,
  setJournal,
  containerVariants,
  itemVariants
}) => {
  const [notes, setNotes] = useState(journal?.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isLisaBoxOpen, setIsLisaBoxOpen] = useState(true);
  const [lisaTip, setLisaTip] = useState<string>('');
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const lastSaveTimeRef = useRef<number>(0);
  
  useEffect(() => {
    setNotes(journal?.notes || '');
    
    // Generate personalized Lisa tip based on journal data
    generateLisaTip();
  }, [journal.id, journal?.notes]);
  
  const generateLisaTip = () => {
    if (!journal) return;
    
    // Check journal scores to provide personalized tips
    if (journal.energyLevel < 5 && journal.restLevel < 5) {
      setLisaTip("Veo que tus niveles de energía y descanso están bajos hoy. ¿Has notado cómo afecta a tu estado de ánimo? Recuerda que puedes contarme cómo te sientes para ayudarte mejor. 💜");
    } else if (journal.waterIntake < 4) {
      setLisaTip("Tu hidratación hoy está un poco baja. ¿Cómo te sientes? Una buena hidratación puede mejorar tu energía y estado de ánimo. Cuéntame si notas cansancio o cambios en tu humor. 💧");
    } else if (journal.stressLevel > 7) {
      setLisaTip("Detecto niveles altos de estrés en tu registro. ¿Quieres contarme más sobre cómo te sientes? Podría recomendarte alimentos que ayuden a regular tu ánimo y estrés. 🧘‍♀️");
    } else {
      setLisaTip("¿Cómo te sientes hoy? Cuéntame tus emociones y sensaciones en tus notas o en nuestro chat, y podré ayudarte a relacionarlas con tu alimentación y hacer recomendaciones personalizadas. 💚");
    }
  };
  
  // Highly throttled function to save notes to Supabase
  const saveNotesToSupabase = useThrottle(async (journalData: NutritionJournal, newNotes: string) => {
    if (!user || user.id.startsWith('user-')) return;
    
    // Check if minimum time between saves has passed
    const now = Date.now();
    if (now - lastSaveTimeRef.current < MIN_SAVE_INTERVAL) {
      console.log(`Skipping save - too soon since last save (${Math.round((now - lastSaveTimeRef.current) / 1000)}s)`);
      return;
    }
    
    // Check if we've exceeded save attempts
    const saveKey = `notes_save_${user.id}_${journalData.date}`;
    const attempts = saveAttempts.get(saveKey) || 0;
    
    if (attempts >= MAX_SAVE_ATTEMPTS) {
      console.warn(`Too many save attempts for notes: ${saveKey}. Skipping save to prevent loop.`);
      // Reset the counter after some time
      setTimeout(() => {
        saveAttempts.delete(saveKey);
      }, SAVE_ATTEMPT_TIMEOUT);
      return;
    }
    
    try {
      setIsSaving(true);
      // Track this attempt
      saveAttempts.set(saveKey, attempts + 1);
      lastSaveTimeRef.current = now;
      
      const { error } = await supabase
        .from('nutrition_journals')
        .upsert({
          id: journalData.id,
          user_id: user.id,
          date: journalData.date,
          notes: newNotes,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        });
        
      if (error) {
        console.error("Error al sincronizar notas con Supabase:", error);
        toast({
          variant: "destructive",
          description: "No se pudieron guardar tus notas en la nube. Se guardarán localmente."
        });
      } else {
        console.log("Notas sincronizadas correctamente con Supabase");
      }
      
      // Reset the counter after some time
      setTimeout(() => {
        saveAttempts.delete(saveKey);
      }, SAVE_ATTEMPT_TIMEOUT);
    } catch (error) {
      console.error("Error en la sincronización de notas:", error);
      toast({
        variant: "destructive",
        description: "Error de conexión. Tus datos se han guardado localmente."
      });
      
      // Still reset the counter after some time
      setTimeout(() => {
        saveAttempts.delete(saveKey);
      }, SAVE_ATTEMPT_TIMEOUT);
    } finally {
      setIsSaving(false);
    }
  }, 15000);  // 15 seconds throttle - very conservative to prevent resource issues
  
  const updateNotes = async (newNotes: string) => {
    setNotes(newNotes);
    
    const updatedJournal = {
      ...journal,
      notes: newNotes,
      updated_at: new Date().toISOString()
    };
    
    setJournal(updatedJournal);
    
    try {
      localStorage.setItem(`journal_${journal.id}`, JSON.stringify(updatedJournal));
      localStorage.setItem(`journal_${user?.id}_${journal.date}`, JSON.stringify(updatedJournal));
    } catch (error) {
      console.error("Error al guardar en localStorage:", error);
    }
    
    // Save to Supabase with strong throttling
    saveNotesToSupabase(journal, newNotes);
  };
  
  const handleLisaAvatarClick = () => {
    triggerLisaChat();
  };
  
  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="grid grid-cols-1 sm:grid-cols-2 gap-5"
    >
      <motion.div variants={itemVariants} className="col-span-1 sm:col-span-2">
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-gradient-to-br from-green-100/50 to-green-50 backdrop-blur-sm border border-green-200 transform transition-all duration-300 hover:shadow-xl">
          <CardHeader className="pb-2 bg-gradient-to-r from-green-500/90 to-green-600/90 text-white">
            <CardTitle className="text-base flex items-center text-white">
              <Info className="w-5 h-5 mr-2" />
              Notas
            </CardTitle>
            <CardDescription className="text-xs text-white/90">Añade tus notas diarias</CardDescription>
          </CardHeader>
          <CardContent className="p-5">
            <Collapsible open={isLisaBoxOpen} onOpenChange={setIsLisaBoxOpen} className="mb-5">
              <div className="bg-gradient-to-br from-purple-100/50 to-purple-50 backdrop-blur-sm border border-purple-200 rounded-lg shadow-sm px-0 py-4">
                <div className="flex justify-between items-start px-2">
                  <div className="flex-shrink-0">
                    <Avatar 
                      className="h-10 w-10 border-2 border-purple-300 cursor-pointer"
                      onClick={handleLisaAvatarClick}
                    >
                      <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                      <AvatarFallback>L</AvatarFallback>
                    </Avatar>
                  </div>
                  <CollapsibleTrigger asChild>
                    <button className="p-1 hover:bg-purple-100 rounded-full">
                      {isLisaBoxOpen ? <ChevronUp className="h-4 w-4 text-purple-500" /> : <ChevronDown className="h-4 w-4 text-purple-500" />}
                    </button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  <p className="text-xs leading-tight text-gray-600 mt-2 px-2">
                    {lisaTip}
                  </p>
                </CollapsibleContent>
              </div>
            </Collapsible>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="notes" className="text-sm font-medium text-green-800">
                    {notes ? "Editar notas" : "Añadir notas"}
                  </Label>
                  {isSaving && (
                    <span className="text-xs text-green-600 animate-pulse">Guardando...</span>
                  )}
                </div>
                <Textarea
                  id="notes"
                  placeholder="Escribe tus notas, pensamientos o como te has sentido hoy..."
                  value={notes}
                  onChange={(e) => updateNotes(e.target.value)}
                  className="border-green-200 min-h-[120px]"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default NotesTab;
